import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './DotNavigation.css';

const DotNavigation = () => {
  const [activeSection, setActiveSection] = useState('about');
  const [progress, setProgress] = useState(0);

  const sections = useMemo(() => [
    { id: 'about', label: 'Home' },
    { id: 'trusted', label: 'About' },
    { id: 'features', label: 'Features' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'contact', label: 'Contact Us' }
  ], []);

  const calculateProgress = useCallback(() => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight - windowHeight;
    const scrolled = window.scrollY;
    return (scrolled / documentHeight) * 100;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      setProgress(calculateProgress());
      
      // Find which section is currently in view
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, calculateProgress]);

  const handleDotClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      setActiveSection(sectionId);
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDotClick(sections[index].id);
    } else if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      document.querySelector(`[data-index="${index - 1}"]`).focus();
    } else if (e.key === 'ArrowDown' && index < sections.length - 1) {
      e.preventDefault();
      document.querySelector(`[data-index="${index + 1}"]`).focus();
    }
  };

  const activeIndex = sections.findIndex(section => section.id === activeSection);

  return (
    <nav className="dot-navigation" role="navigation" aria-label="Page Navigation">
      <div className="progress-bar" style={{ height: `${progress}%` }} />
      {sections.map((section, index) => (
        <div key={section.id} className="dot-container">
          <button
            className={`dot-nav-item ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => handleDotClick(section.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            aria-label={`Scroll to ${section.label} section`}
            aria-current={activeSection === section.id ? 'true' : 'false'}
            data-index={index}
            tabIndex={0}
          >
            <span className="dot-tooltip">{section.label}</span>
          </button>
          {index < sections.length - 1 && (
            <div 
              className={`dot-connector ${index < activeIndex ? 'passed' : ''} ${index === activeIndex ? 'active' : ''}`}
              aria-hidden="true"
            />
          )}
        </div>
      ))}
    </nav>
  );
};

export default DotNavigation; 